/**
 * What a person looks like: their build, their hair, their face, and what they
 * have on. Pure, and the same every time for the same person.
 *
 * Read off the plain row the game hands the drawing layer (department, whether
 * they are crew, a gate, a guest or a visitor, and an optional `look` written
 * on the row of anybody who must be recognised on sight). Nothing here is a
 * rule: every number is drawing. The capsule everybody is collided and seen as
 * is untouched by all of it, which is why a build can vary at all
 * (`figure.js` scales bones, never the body the game moves).
 *
 * Staff wear their department, and a department still reads off the body,
 * which is what its colour is for (`data/departments.js`): chef whites with the
 * Kitchen's red at the neck, the Housekeeping tunic, the Valet jacket, the
 * Front Desk blazer, the Engineering work shirt and tool belt, the Management
 * suit, and Banquets' vest over a white shirt. Guests wear their own clothes,
 * from a palette that keeps clear of every department's colour, and never a
 * uniform piece: no name tag, no tie, no apron, no tool belt, no toque.
 */

/** Skin tones, light to dark. */
export const SKINS = Object.freeze([0xf3cfb0, 0xe8bf9c, 0xe2b089, 0xc98f67, 0xa06c4a, 0x8a5a3c, 0x7a4b31, 0x55331f]);

/** Hair colours by name, so a row can ask for one in words. */
export const HAIR_COLOURS = Object.freeze({
  black: 0x17140f,
  darkbrown: 0x33241a,
  brown: 0x5e3e24,
  auburn: 0x7a3a1e,
  red: 0x9a4a22,
  blond: 0xb08d55,
  sandy: 0x9a7c52,
  grey: 0x8f8b86,
  silver: 0xc4c1bb,
  white: 0xe2ded8,
});

/** The dozen hairstyles `dress.js` builds, and bald. */
export const HAIRSTYLES = Object.freeze(['buzz', 'crop', 'sidepart', 'slick', 'quiff', 'curly', 'afro', 'bun', 'ponytail', 'long', 'bob', 'hightop']);

/** Facial hair, by name. */
export const FACES = Object.freeze(['stubble', 'moustache', 'goatee', 'beard']);

/**
 * How far a build may go, as scales on the bones, either side of one. Kept
 * where a body still fits its own clothes and its own clips: a hand still
 * reaches the hip it was animated to reach, a head still clears a door.
 */
export const BUILD_LIMITS = Object.freeze({
  height: [0.94, 1.05],
  chest: [0.92, 1.14],
  belly: [0.94, 1.2],
  hips: [0.94, 1.12],
  shoulders: [0.92, 1.1],
});

/** What a guest may wear: nothing within reach of a department's colour, and no black or white suit. */
const GUEST_TOPS = Object.freeze([0xf0e6d2, 0x8fb6d8, 0xe9a4a0, 0xf2c879, 0x9fcf9a, 0xd9c2e8, 0x6fa6a0, 0xf4f0e6, 0xe57a52, 0x3e6ea8, 0xc9b49a, 0xe7d3a0, 0x88c0c8, 0xb4475a]);
const GUEST_BOTTOMS = Object.freeze([0x3d5577, 0x2f3d55, 0xc8b48c, 0x8a7a62, 0xe6dccb, 0x5a5f66, 0x6d5844, 0xd8cdb6]);
const SWIM = Object.freeze([0x2a7fc0, 0xe0503a, 0x2fa38a, 0xf2b630, 0x7a4fb0, 0x1d2b44]);

/** The uniform pieces only staff ever have; a guest's look is held to never containing one. */
export const UNIFORM_PIECES = Object.freeze(['tag', 'tie', 'bowtie', 'apron', 'toolbelt', 'toque', 'neckerchief', 'lapels', 'buttons', 'vest', 'valetcap']);

function hashOf(key) {
  const text = String(key ?? '');
  let h = 2166136261;
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** A little generator off a key: the same numbers in the same order, every time. */
function dice(key) {
  let state = hashOf(key) || 1;
  const next = () => {
    state ^= state << 13;
    state >>>= 0;
    state ^= state >>> 17;
    state ^= state << 5;
    state >>>= 0;
    return state / 4294967296;
  };
  return {
    next,
    pick: (list) => list[Math.floor(next() * list.length)],
    range: ([lo, hi]) => lo + (hi - lo) * next(),
    chance: (p) => next() < p,
  };
}

const mixHex = (a, b, t) => {
  const ch = (shift) => Math.round(((a >> shift) & 255) * (1 - t) + ((b >> shift) & 255) * t);
  return (ch(16) << 16) | (ch(8) << 8) | ch(0);
};

/** A middle-of-the-range build, rolled within the limits, a little toward the middle. */
function rollBuild(d) {
  const toward = ([lo, hi]) => {
    const t = (d.next() + d.next()) / 2;
    return lo + (hi - lo) * t;
  };
  const heavy = d.next();
  return {
    height: toward(BUILD_LIMITS.height),
    chest: 1 + (toward(BUILD_LIMITS.chest) - 1) * 0.6 + (heavy - 0.5) * 0.08,
    belly: 1 + (toward(BUILD_LIMITS.belly) - 1) * (0.5 + heavy),
    hips: toward(BUILD_LIMITS.hips),
    shoulders: toward(BUILD_LIMITS.shoulders),
  };
}

function clampBuild(build) {
  const out = {};
  for (const [part, [lo, hi]] of Object.entries(BUILD_LIMITS)) {
    out[part] = Math.min(hi, Math.max(lo, build[part] ?? 1));
  }
  return out;
}

/**
 * What each department wears, given a person's dice. `colour` is the
 * department's own; every uniform keeps it somewhere a glance lands on.
 */
const UNIFORMS = {
  banquets: (d, colour, row) => ({
    kind: 'banquets',
    torso: mixHex(colour, 0x222222, 0.15),
    sleeves: 0xf1efe9,
    legs: 0x1c1c20,
    sleeveEnd: d.chance(0.25) ? 'rolled' : 'long',
    pieces: row.gate || /capt|lead/.test(row.id ?? '') ? ['vest', 'tie', 'tag'] : ['vest', 'bowtie', 'tag'],
    tie: 0x1a1a1a,
  }),
  kitchen: (d, colour, row) => ({
    kind: 'chef',
    torso: 0xffffff,
    sleeves: 0xffffff,
    legs: d.chance(0.5) ? 0x2a2a2e : 0x4a4a50,
    sleeveEnd: d.chance(0.35) ? 'rolled' : 'long',
    pieces: ['buttons', 'neckerchief', 'apron', 'tag', ...(row.gate || d.chance(0.6) ? ['toque'] : [])],
    tie: colour,
    apron: 0xefece6,
    buttons: 0x2b2b2b,
  }),
  housekeeping: (d, colour) => ({
    kind: 'tunic',
    torso: colour,
    sleeves: colour,
    legs: mixHex(colour, 0x101418, 0.7),
    sleeveEnd: 'short',
    pieces: ['apron', 'buttons', 'tag'],
    apron: 0xf0eee8,
    buttons: 0xf0eee8,
  }),
  valet: (d, colour) => ({
    kind: 'valet',
    torso: colour,
    sleeves: colour,
    legs: 0x1b1d24,
    sleeveEnd: 'long',
    pieces: ['buttons', 'lapels', 'tag', ...(d.chance(0.5) ? ['valetcap'] : [])],
    buttons: 0xd8b25a,
    lapel: mixHex(colour, 0x000000, 0.35),
    cap: mixHex(colour, 0x000000, 0.25),
  }),
  frontdesk: (d, colour) => ({
    kind: 'blazer',
    torso: colour,
    sleeves: colour,
    legs: d.chance(0.5) ? 0x2c2a33 : 0x474552,
    sleeveEnd: 'long',
    pieces: ['lapels', 'shirt', 'tag', ...(d.chance(0.5) ? ['tie'] : [])],
    lapel: mixHex(colour, 0x000000, 0.3),
    shirt: 0xf4f2ee,
    tie: mixHex(colour, 0xffffff, 0.35),
  }),
  engineering: (d, colour) => ({
    kind: 'workshirt',
    torso: colour,
    sleeves: colour,
    legs: 0x2b3442,
    sleeveEnd: d.chance(0.5) ? 'rolled' : 'long',
    pieces: ['toolbelt', 'buttons', 'tag'],
    buttons: mixHex(colour, 0x000000, 0.5),
    belt: 0x5a3b22,
  }),
  management: (d, colour) => {
    const suit = d.pick([0x2e3440, 0x3a3f4a, 0x23262e, 0x4a505c]);
    return {
      kind: 'suit',
      torso: suit,
      sleeves: suit,
      legs: suit,
      sleeveEnd: 'long',
      pieces: ['lapels', 'shirt', 'tie', 'tag'],
      lapel: mixHex(suit, 0x000000, 0.3),
      shirt: 0xf4f4f2,
      // The department's colour is the tie: the one thing on a suit that is anybody's.
      tie: d.pick([colour, 0x7a2430, 0x2c4a7a, colour]),
    };
  },
};

function guestOutfit(d, row) {
  if (row.wading) {
    return { kind: 'swim', torso: null, sleeves: null, legs: d.pick(SWIM), sleeveEnd: 'none', legEnd: 'shorts', pieces: [] };
  }
  const resort = d.chance(0.55);
  const top = d.pick(GUEST_TOPS);
  const bottom = d.pick(GUEST_BOTTOMS);
  const pieces = [];
  if (resort && d.chance(0.35)) pieces.push('sunhat');
  else if (d.chance(0.2)) pieces.push('cap');
  if (d.chance(resort ? 0.4 : 0.15)) pieces.push('sunglasses');
  return {
    kind: resort ? 'resort' : 'casual',
    torso: top,
    sleeves: top,
    legs: bottom,
    sleeveEnd: resort || d.chance(0.5) ? 'short' : 'long',
    legEnd: resort && d.chance(0.6) ? 'shorts' : 'long',
    pieces,
    hat: d.pick([0xe8dcc0, 0xf2efe6, 0x3b4a5e, 0xc9a978]),
  };
}

/** A visitor from the ownership group dresses like money and wears no badge. */
function visitorOutfit(d) {
  return {
    kind: 'suit',
    torso: 0x9aa0a8,
    sleeves: 0x9aa0a8,
    legs: 0x9aa0a8,
    sleeveEnd: 'long',
    pieces: ['lapels', 'shirt', 'tie'],
    lapel: 0x7d838c,
    shirt: 0xf6f6f4,
    tie: 0x1f3a6a,
  };
}

/**
 * The whole look for one person.
 *
 * `row` is their roster row, as plain data: `id`, `department`, `crew`,
 * `gate`, `visitor`, `wading`, `name`, and `look` if they have one.
 * `colour` is their department's colour, or null.
 */
export function appearanceFor(row, colour = null) {
  const d = dice(`look4:${row.id ?? row.name ?? ''}`);
  const fixed = row.look ?? {};
  const guest = !row.visitor && !colour;

  const skin = fixed.skin !== undefined ? SKINS[fixed.skin] ?? fixed.skin : d.pick(SKINS);
  const older = d.chance(guest ? 0.25 : 0.15);
  const hairColour =
    fixed.hairColour !== undefined
      ? HAIR_COLOURS[fixed.hairColour] ?? fixed.hairColour
      : older
        ? d.pick([HAIR_COLOURS.grey, HAIR_COLOURS.silver, HAIR_COLOURS.white])
        : d.pick([HAIR_COLOURS.black, HAIR_COLOURS.black, HAIR_COLOURS.darkbrown, HAIR_COLOURS.darkbrown, HAIR_COLOURS.brown, HAIR_COLOURS.auburn, HAIR_COLOURS.blond, HAIR_COLOURS.sandy, HAIR_COLOURS.red]);
  const style = fixed.hair ?? (d.chance(0.06) ? 'bald' : d.pick(HAIRSTYLES));
  const face = fixed.face !== undefined ? fixed.face : d.chance(0.32) ? d.pick(FACES) : null;
  const build = clampBuild({ ...rollBuild(d), ...(fixed.build ?? {}) });

  let outfit;
  if (row.visitor) outfit = visitorOutfit(d);
  else if (guest) outfit = guestOutfit(d, row);
  else outfit = (UNIFORMS[row.department] ?? UNIFORMS.banquets)(d, colour, row);
  outfit = { legEnd: 'long', ...outfit, ...(fixed.outfit ?? {}) };
  const pieces = new Set([...(outfit.pieces ?? []), ...(fixed.pieces ?? [])]);
  for (const piece of fixed.without ?? []) pieces.delete(piece);
  // A guest never has a uniform piece, whatever was asked for.
  if (guest) for (const piece of UNIFORM_PIECES) pieces.delete(piece);
  if (fixed.glasses) pieces.add('glasses');
  else if (!guest && d.chance(0.12) && !pieces.has('sunglasses')) pieces.add('glasses');

  return {
    key: row.id ?? row.name ?? '',
    build,
    skin,
    hair: { style, colour: hairColour },
    face,
    outfit: { ...outfit, pieces: [...pieces] },
    tag: pieces.has('tag') ? { stripe: colour ?? 0xc8a15a } : null,
  };
}
